import { ButtonHTMLAttributes, ReactNode, MouseEvent } from 'react';
import ButtonStyle from './button.style';

export type ButtonVariant = 'textButton' | 'outlined' | 'fab' | 'extendedFab';
export type ButtonColors =
  | 'primary'
  | 'secondary'
  | 'warning'
  | 'error'
  | 'primaryWithBg'
  | 'secondaryWithBg'
  | 'warningWithBg'
  | 'errorWithBg';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title' | 'type'> {
  type?: 'button' | 'submit' | 'reset';
  title?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  iconPosition?: 'left' | 'right';
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  loader?: ReactNode;
  loaderColor?: string;
  isMaterial?: boolean;
  isLoading?: boolean;
  className?: string;
  variant?: ButtonVariant;
  colors?: ButtonColors;
}

const SimpleSpinner = ({ color = '#30C56D' }: { color?: string }) => (
  <span
    className="btn-spinner"
    style={{
      display: 'inline-block',
      width: 16,
      height: 16,
      border: '2px solid currentColor',
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'btn-spin 0.6s linear infinite',
    }}
  />
);

const Button = ({
  type = 'button',
  title,
  icon,
  disabled = false,
  iconPosition = 'right',
  onClick,
  loader,
  loaderColor,
  isMaterial = false,
  isLoading = false,
  className,
  ...props
}: ButtonProps) => {
  const addAllClasses = ['reusecore__button'];
  if (isLoading) addAllClasses.push('is-loading');
  if (isMaterial) addAllClasses.push('is-material');
  if (className) addAllClasses.push(className);

  const buttonIcon = isLoading
    ? loader ?? <SimpleSpinner color={loaderColor} />
    : icon && <span className="btn-icon">{icon}</span>;

  return (
    <ButtonStyle
      type={type}
      className={addAllClasses.join(' ')}
      icon={icon}
      disabled={disabled}
      icon-position={iconPosition}
      onClick={onClick}
      {...props}
    >
      {iconPosition === 'left' && buttonIcon}
      {title && <span className="btn-text">{title}</span>}
      {iconPosition === 'right' && buttonIcon}
    </ButtonStyle>
  );
};

export default Button;
